import { useEffect } from "react";
import { useHashRoute, navigate } from "./router";
import { useAuth } from "./context/AuthContext";
import { useLang } from "./context/LangContext";
import { BACKEND_ENABLED } from "./config";
import { Layout } from "./components/Layout";
import { HomePage } from "./pages/HomePage";
import { DocsPage } from "./pages/DocsPage";
import { FaqPage } from "./pages/FaqPage";
import { DownloadsPage } from "./pages/DownloadsPage";
import { LoginPage } from "./pages/LoginPage";

// Apply the saved theme on mount.
function useTheme() {
  useEffect(() => {
    const saved = localStorage.getItem("survantic_support_theme");
    const prefersDark =
      window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const dark = saved ? saved === "dark" : prefersDark;
    document.documentElement.classList.toggle("dark-mode", dark);
  }, []);
}

export default function App() {
  useTheme();
  const route = useHashRoute();
  const { session, loading } = useAuth();
  const { t } = useLang();

  const top = route.segments[0] ?? "";

  // Backend-coupled routes only exist when a backend is wired (see config.ts).
  // On the static public deploy they don't exist → fall through to "not found".
  const backendRoute = top === "downloads" || top === "login";
  if (backendRoute && !BACKEND_ENABLED) {
    return (
      <Layout>
        <div className="center-pad">
          <h2>{t.pageNotFound}</h2>
          <p className="muted">
            {t.pageNotFoundBody}
            <code>{route.path}</code>.
          </p>
        </div>
      </Layout>
    );
  }

  // Downloads require a signed-in tenant user. Docs and FAQ are public.
  const gated = top === "downloads";

  if (loading) {
    return (
      <Layout>
        <div className="center-pad muted">{t.loading}</div>
      </Layout>
    );
  }

  if (top === "login") {
    return (
      <Layout>
        <LoginPage />
      </Layout>
    );
  }

  if (gated && !session) {
    navigate(`/login?next=${encodeURIComponent(route.path)}`);
    return (
      <Layout>
        <LoginPage />
      </Layout>
    );
  }

  return (
    <Layout>
      {top === "" && <HomePage />}
      {top === "docs" && <DocsPage route={route} />}
      {top === "faq" && <FaqPage route={route} />}
      {top === "downloads" && <DownloadsPage />}
      {!["", "docs", "faq", "downloads", "login"].includes(top) && (
        <div className="center-pad">
          <h2>{t.pageNotFound}</h2>
          <p className="muted">
            {t.pageNotFoundBody}
            <code>{route.path}</code>.
          </p>
        </div>
      )}
    </Layout>
  );
}
